import { useState, useEffect, useCallback, useRef } from "react";

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

interface UseApiResult<T> extends UseApiState<T> {
  refetch: () => Promise<T>;
}

export function useApi<T>(fetcher: () => Promise<T>, deps: unknown[] = []): UseApiResult<T> {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });
  const mountedRef = useRef(true);
  const requestSequenceRef = useRef(0);

  const execute = useCallback(() => {
    const requestSequence = ++requestSequenceRef.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    return fetcher()
      .then((data) => {
        if (mountedRef.current && requestSequence === requestSequenceRef.current) {
          setState({ data, loading: false, error: null });
        }
        return data;
      })
      .catch((error: Error) => {
        if (mountedRef.current && requestSequence === requestSequenceRef.current) {
          setState((previous) => ({ data: previous.data, loading: false, error }));
        }
        throw error;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    mountedRef.current = true;
    execute().catch(() => {});
    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, [execute]);

  return { ...state, refetch: execute };
}
